import axios from 'axios';

/**
 * Serviço de Logging do Frontend
 * Envia logs para o backend em vez de usar console.log
 */
class LoggerService {
  constructor() {
    // Usar URL relativa - funciona em dev (proxy Vite) e produção (Nginx)
    this.apiUrl = '/api';
    this.enabled = true;
    this.queue = [];
    this.isProcessing = false;
    this.maxRetries = 3;
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 segundos
    
    // ✅ Proteção contra loop infinito de 429
    this.rateLimitHit = false;
    this.rateLimitResetTime = null;

    // Iniciar flush automático
    this.startAutoFlush();

    // Capturar erros não tratados
    this.setupErrorHandlers();
  }

  /**
   * Envia log para o backend
   */
  async sendLog(level, category, source, message, details = {}, error = null) {
    if (!this.enabled) {
      // Em desenvolvimento, também mostrar no console
      if (import.meta.env.DEV) {
        console.log(`[${level}] [${category}] ${source}: ${message}`, details);
      }
      return;
    }

    const logData = {
      level,
      category,
      source,
      message,
      details: {
        ...details,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      },
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: error.code,
        isHandled: error.isHandled !== undefined ? error.isHandled : true
      } : null
    };

    // Adicionar à fila
    this.queue.push(logData);

    // Se a fila estiver cheia, processar imediatamente
    if (this.queue.length >= this.batchSize) {
      await this.flush();
    }
  }

  /**
   * Processa a fila de logs
   */
  async flush() {
    if (this.isProcessing || this.queue.length === 0) return;

    // ✅ PROTEÇÃO: Se rate limit ativo, verificar se já passou o tempo
    if (this.rateLimitHit) {
      const now = Date.now();
      if (this.rateLimitResetTime && now < this.rateLimitResetTime) {
        // Ainda em rate limit, descartar logs silenciosamente
        if (import.meta.env.DEV) {
          console.warn(`⚠️ LoggerService: Rate limit ativo. Descartando ${this.queue.length} logs. Reset em ${Math.ceil((this.rateLimitResetTime - now) / 1000)}s`);
        }
        this.queue = []; // Limpar fila
        return;
      } else {
        // Rate limit expirou, resetar
        this.rateLimitHit = false;
        this.rateLimitResetTime = null;
        if (import.meta.env.DEV) {
          console.log('✅ LoggerService: Rate limit resetado, retomando envio de logs');
        }
      }
    }

    this.isProcessing = true;
    const logsToSend = this.queue.splice(0, this.batchSize);

    try {
      // Enviar logs em lote
      await Promise.all(
        logsToSend.map(log =>
          axios.post(`${this.apiUrl}/logs/client`, log, {
            timeout: 10000, // 10 segundos - aumentado para evitar timeouts
            withCredentials: true, // ✅ Enviar cookies automaticamente
            headers: {
              'Content-Type': 'application/json'
            }
          }).catch(err => {
            // ✅ PROTEÇÃO: Detectar erro 429 (Too Many Requests)
            if (err.response && err.response.status === 429) {
              this.rateLimitHit = true;
              // Rate limit por 60 segundos
              this.rateLimitResetTime = Date.now() + 60000;
              
              if (import.meta.env.DEV) {
                console.warn('🚨 LoggerService: Rate limit (429) detectado. Pausando logs por 60 segundos.');
              }
              
              // Limpar fila para evitar acúmulo
              this.queue = [];
              return;
            }
            
            // Se falhar por outro motivo, mostrar no console como fallback
            if (import.meta.env.DEV && err.response?.status !== 429) {
              console.error('Erro ao enviar log:', err.message);
            }
          })
        )
      );
    } catch (error) {
      // Falha silenciosa - não queremos quebrar a aplicação por causa de logs
      if (import.meta.env.DEV) {
        console.error('Erro ao processar fila de logs:', error.message);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Inicia flush automático
   */
  startAutoFlush() {
    setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush antes de fechar a página
    window.addEventListener('beforeunload', () => {
      if (this.queue.length > 0) {
        // Usar sendBeacon para enviar logs de forma síncrona antes de fechar
        const data = JSON.stringify(this.queue);
        navigator.sendBeacon(`${this.apiUrl}/logs/client`, data);
        this.queue = [];
      }
    });
  }

  /**
   * Configura handlers para erros não tratados
   */
  setupErrorHandlers() {
    // Erros JavaScript não tratados
    window.addEventListener('error', (event) => {
      this.error('frontend', 'window.onerror', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }, event.error);
    });

    // Promises rejeitadas não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      this.error('frontend', 'unhandledrejection', 'Promise rejeitada não tratada', {
        reason: event.reason
      }, event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
    });
  }

  // Métodos de conveniência
  debug(category, source, message, details = {}) {
    return this.sendLog('DEBUG', category, source, message, details);
  }

  info(category, source, message, details = {}) {
    return this.sendLog('INFO', category, source, message, details);
  }

  warn(category, source, message, details = {}) {
    return this.sendLog('WARN', category, source, message, details);
  }

  error(category, source, message, details = {}, error = null) {
    return this.sendLog('ERROR', category, source, message, details, error);
  }

  fatal(category, source, message, details = {}, error = null) {
    return this.sendLog('FATAL', category, source, message, details, error);
  }

  /**
   * Desabilita o logger (útil para testes)
   */
  disable() {
    this.enabled = false;
  }

  /**
   * Habilita o logger
   */
  enable() {
    this.enabled = true;
  }
}

// Exportar instância única (Singleton)
export default new LoggerService();
